import {
  REALTIME_KEEPALIVE_INTERVAL_MS,
  type CanvasOp,
  type ClientMessage,
  type ServerMessage,
} from "@refinement-canvas/domain";
import type { RealtimeClient } from "./realtime-client";

interface CreateOptions {
  readonly websocketUrl: string;
  readonly createSocket?: ((url: string) => WebSocket) | undefined;
  /**
   * keepalive ping の送信間隔（ミリ秒）。テストでの上書き用。
   * 既定は API Gateway WebSocket のアイドルタイムアウト（10 分）より十分短い 5 分。
   */
  readonly keepAliveIntervalMs?: number | undefined;
  /** ping 送信後に pong / 任意の受信を待つ許容時間（ミリ秒）。テストでの上書き用。 */
  readonly pongTimeoutMs?: number | undefined;
  /** 一時メッセージ送信待ちキューの上限。テストでの上書き用。 */
  readonly maxPendingTransientMessages?: number | undefined;
}

const SOCKET_OPEN = 1;
// 一時メッセージ（viewer.sync / op.transient）の送信待ち上限。古いものから捨ててよい。
// 永続 op（op.apply）はこの上限とは無関係に、切断中でも一切捨てない（別キューで管理する）。
const DEFAULT_MAX_PENDING_TRANSIENT_MESSAGES = 200;
// API Gateway WebSocket は 10 分間メッセージの送受信が無いと接続を切断する。
// その手前で定期的に ping を送り、編集中・閲覧中の接続が無言で切れるのを防ぐ。
// 値はサーバ側の接続登録 TTL と対で不変条件（TTL >= ping 間隔 * 3）を検証できるよう、
// packages/domain の realtime 定数を単一正本として参照する。
const DEFAULT_KEEPALIVE_INTERVAL_MS = REALTIME_KEEPALIVE_INTERVAL_MS;
// ping 送信後、pong（または任意のサーバメッセージ）が届くまでの許容時間。超過したら
// half-open（相手には届かないのに readyState は OPEN のままで onclose も発火しない）接続と
// みなして自分から切断し、再接続経路へ乗せる。PC スリープ復帰・プロキシ切断・ネットワーク
// 切替の後に「画面は表示されたまま同期だけが silently 止まる」事象（2026-07-06 本番障害）を
// クライアント側から検知するための仕組み。
const DEFAULT_PONG_TIMEOUT_MS = 10 * 1000;

// 永続させる必要があるメッセージ（サーバへ必ず届けたい op）か判定する。
// op.apply は永続 op の送信であり、切断中も絶対に捨ててはいけない。
// viewer.sync / op.transient / bootstrap.request は最新だけ意味がある一時メッセージで、
// 送信待ちが溢れたら古いものから捨ててよい（カーソル等は再接続時に作り直される）。
function isPersistentMessage(message: ClientMessage): boolean {
  return message.type === "op.apply";
}

// WebSocket 経由のリアルタイム通信クライアント。
// PO Board の websocket-realtime.ts を踏襲し、サーバ権威キュー方式に拡張する。
// - 接続: GET /api/canvases/:id/realtime-session でチケット発行 → wss?ticket=... で接続
// - 送信: 永続 op は op.apply、一時 op は op.transient で送信
// - 受信: viewer.* / op.commit / op.transient / op.reject / bootstrap.snapshot / resync.required
// - 接続維持: 接続中は keepalive ping を定期送信し、アイドルタイムアウトでの切断を防ぐ
// - 切断検知: 確立後の予期せぬ切断は onClose で通知し、呼び出し側（フック）が再接続する
export function createWebSocketRealtimeClient(options: CreateOptions): RealtimeClient {
  let socket: WebSocket | null = null;
  const handlers = new Set<(message: ServerMessage) => void>();
  const closeHandlers = new Set<() => void>();
  const reopenHandlers = new Set<() => void>();
  // 送信待ちを永続 op と一時メッセージで完全に分離する。
  // 永続 op（op.apply）は切断中でも絶対に捨てない。一時メッセージ（viewer.sync / op.transient）は
  // 溢れたら古いものから捨ててよい（カーソル等は最新だけ意味があり、再接続時に作り直される）。
  // 永続 op が一時メッセージの洪水で押し出される事故を構造的に防ぐ。
  const pendingOps: ClientMessage[] = [];
  const pendingTransient: ClientMessage[] = [];
  const maxPendingTransientMessages =
    options.maxPendingTransientMessages ?? DEFAULT_MAX_PENDING_TRANSIENT_MESSAGES;
  const keepAliveIntervalMs = options.keepAliveIntervalMs ?? DEFAULT_KEEPALIVE_INTERVAL_MS;
  const pongTimeoutMs = options.pongTimeoutMs ?? DEFAULT_PONG_TIMEOUT_MS;
  let keepAliveTimer: ReturnType<typeof setInterval> | null = null;
  // ping 送信後の pong 待ちタイマー。何かしらのサーバメッセージ受信（pong に限らない）で解除する。
  let pongDeadlineTimer: ReturnType<typeof setTimeout> | null = null;
  // disconnect() による意図的な切断と、予期せぬ切断（タイムアウト等）を区別するためのフラグ。
  let closedByUser = false;
  // 一度でも接続が確立したか。2 回目以降の open（再接続）でのみ onReopen を発火させるために使う。
  let hasConnectedBefore = false;
  // visibilitychange / online の復帰時プローブ用リスナー。多重登録を避けるため参照を保持する。
  let wakeProbeListener: (() => void) | null = null;
  // サーバが ping に pong を返す実装か（一度でも pong を観測したか）。pong 非対応の旧サーバに
  // 接続している間に pong タイムアウトで切断すると、アイドル時に 5 分ごとの再接続ループになる
  // （ローリングデプロイ中の新クライアント×旧サーバ混在への後方互換）。pong を観測するまでは
  // 死活監視（deadline）を武装しない。
  let pongCapabilityObserved = false;

  const clearPongDeadline = (): void => {
    if (pongDeadlineTimer !== null) {
      clearTimeout(pongDeadlineTimer);
      pongDeadlineTimer = null;
    }
  };

  // ping を送り、pong（または任意の受信メッセージ）が pongTimeoutMs 以内に届かなければ
  // half-open 接続とみなして自分から切断する。既に pong 待ちが進行中なら二重にはしない。
  // pong を返さない旧サーバに対しては deadline を張らない（送信のみ＝従来の keepalive 動作）。
  const sendPingWithDeadline = (): void => {
    if (socket?.readyState !== SOCKET_OPEN) return;
    socket.send(JSON.stringify({ type: "ping" } satisfies ClientMessage));
    if (!pongCapabilityObserved) return;
    if (pongDeadlineTimer !== null) return;
    pongDeadlineTimer = setTimeout(() => {
      pongDeadlineTimer = null;
      console.warn("[realtime] ping への応答がありません。接続が失われたとみなして再接続します。");
      forceReconnect();
    }, pongTimeoutMs);
  };

  const startKeepAlive = (): void => {
    stopKeepAlive();
    keepAliveTimer = setInterval(() => {
      // 開いている間だけ ping を送る。アイドルタイムアウト回避と同時に、サーバ側の接続登録
      // （presence）の TTL 延長・half-open 検知（pong 待ち）を兼ねる。
      sendPingWithDeadline();
    }, keepAliveIntervalMs);
  };

  const stopKeepAlive = (): void => {
    if (keepAliveTimer !== null) {
      clearInterval(keepAliveTimer);
      keepAliveTimer = null;
    }
    clearPongDeadline();
  };

  // 予期せぬ生存不能状態（pong タイムアウト / connection.stale 受信）から、onclose を待たずに
  // 自分から切断して再接続経路（onClose 購読者 = use-realtime-connection の再接続処理）へ乗せる。
  // half-open ではブラウザの onclose が長時間（場合によっては永遠に）発火しないため必須。
  const forceReconnect = (): void => {
    if (closedByUser) return;
    const current = socket;
    if (!current) return;
    // 先に socket を外して、current.close() 由来の遅延 onclose が二重通知しないようにする
    // （onclose 側は socket === activeSocket のときだけ通知する）。
    socket = null;
    stopKeepAlive();
    try {
      current.close();
    } catch {
      // 既に閉じている場合などは無視する。
    }
    closeHandlers.forEach((h) => h());
  };

  // タブ復帰・ネットワーク復帰の直後に即時 ping を送り、スリープ中に死んだ接続を数秒で検知する
  // （keepalive の 5 分周期を待たない）。スリープ中は setInterval ごと止まるため、復帰イベントが
  // 唯一の早期検知契機になる。テスト環境（document 無し）では何もしない。
  const registerWakeProbe = (): void => {
    if (wakeProbeListener !== null) return;
    if (typeof document === "undefined" || typeof window === "undefined") return;
    const probe = (): void => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      // 復帰時は凍結中に張られたまま残った旧 deadline を破棄してから ping を送り直す。
      // そうしないと sendPingWithDeadline が新しい deadline を張れず（pongDeadlineTimer!==null で早期 return）、
      // 期限切れの旧 deadline が復帰直後に即発火し、生存中の接続を誤って切断してしまう。
      clearPongDeadline();
      sendPingWithDeadline();
    };
    document.addEventListener("visibilitychange", probe);
    window.addEventListener("online", probe);
    wakeProbeListener = () => {
      document.removeEventListener("visibilitychange", probe);
      window.removeEventListener("online", probe);
    };
  };

  const unregisterWakeProbe = (): void => {
    wakeProbeListener?.();
    wakeProbeListener = null;
  };

  const connect = async (canvasId: string, ticket?: string): Promise<void> => {
    // 既存ソケットがあれば（再接続のやり直し等）、onClose を発火させずに静かに閉じてから張り替える。
    // 先に socket を null にすることで、旧ソケットの onclose ガード（socket === activeSocket）を
    // 無効化し、張り替え時に予期せぬ close 通知や二重接続が起きないようにする。
    if (socket) {
      const previous = socket;
      socket = null;
      stopKeepAlive();
      try {
        previous.close();
      } catch {
        // 既に閉じている場合などは無視する。
      }
    }
    closedByUser = false;
    // pong 対応の観測は「接続ごと」に取り直す。デプロイのロールバック・新旧サーバー混在中は
    // 再接続先が pong 非対応の旧サーバーになり得るため、前の接続で観測した capability を
    // 持ち越すと旧サーバー相手に pong タイムアウトの切断ループを起こす。
    pongCapabilityObserved = false;
    const params = new URLSearchParams({ canvasId });
    if (ticket) params.set("ticket", ticket);
    const url = `${options.websocketUrl}?${params.toString()}`;
    const activeSocket = (
      options.createSocket ?? ((socketUrl: string) => new WebSocket(socketUrl))
    )(url);
    socket = activeSocket;
    // この接続が一度でも open まで到達したか。open 前後で close の扱いを分けるために使う。
    let opened = false;
    return new Promise((resolve, reject) => {
      activeSocket.onopen = () => {
        opened = true;
        const isReconnect = hasConnectedBefore;
        hasConnectedBefore = true;
        startKeepAlive();
        registerWakeProbe();
        flushPendingMessages();
        // 再接続（2 回目以降の open）では、presence の再登録などを購読者に促す。
        if (isReconnect) reopenHandlers.forEach((h) => h());
        resolve();
      };
      // open 前の error のみ接続失敗として reject する（open 後の error は close 経由で扱う）。
      activeSocket.onerror = (event) => {
        if (!opened) reject(event);
      };
      activeSocket.onclose = () => {
        // keepAliveTimer はクライアントで 1 個共有のため、旧ソケットの遅延 close が
        // 新ソケット用の keepalive を止めないよう、自分が現役ソケットのときだけ停止する。
        if (socket === activeSocket) stopKeepAlive();
        if (!opened) {
          // 接続確立前の切断は接続失敗として扱う。
          reject(new Error("WebSocket 接続が確立前に切断されました。"));
          return;
        }
        // 確立後の予期せぬ切断（アイドルタイムアウト / ネットワーク断 / 接続時間上限など）。
        // disconnect() による意図的な切断でなければ、購読者へ通知して再接続を促す。
        // すでに別ソケットへ張り替えられている場合（再接続後の旧ソケット close）は通知しない。
        if (!closedByUser && socket === activeSocket) {
          closeHandlers.forEach((h) => h());
        }
      };
      activeSocket.onmessage = (event) => {
        // すでに別ソケットへ張り替えられている場合（再接続後の旧ソケットの遅延メッセージ）は破棄する。
        // 旧ソケットの遅れて届く viewer.snapshot / cursor / view 等が、新ソケットで再同期した直後の
        // 状態を上書きするのを防ぐ（onclose の現役ソケット判定と対称）。
        if (socket !== activeSocket) return;
        // 何かしら届いた＝接続は生きている。pong 待ちを解除する（pong に限定しないのは、
        // pong より先に op.commit 等が届くケースを誤って切断しないため）。
        clearPongDeadline();
        try {
          const message = JSON.parse(event.data) as ServerMessage;
          // 接続維持系のメッセージはこの層で完結させ、アプリ層へは流さない。
          if (message.type === "pong") {
            // pong 対応サーバと確定。以後の ping では half-open 死活監視（deadline）を武装する。
            pongCapabilityObserved = true;
            return;
          }
          if (message.type === "connection.stale") {
            // サーバ側の接続登録（presence）が失効している。ソケットは生きていても op / presence が
            // 全て破棄される状態のため、即座に再接続（$connect での再登録＋再 bootstrap）へ乗せる。
            console.warn(
              "[realtime] サーバの接続登録が失効しています。再接続します:",
              message.reason
            );
            forceReconnect();
            return;
          }
          handlers.forEach((h) => h(message));
        } catch (e) {
          console.warn("[realtime] invalid message", e);
        }
      };
    });
  };

  const disconnect = (): void => {
    closedByUser = true;
    stopKeepAlive();
    unregisterWakeProbe();
    if (socket?.readyState === SOCKET_OPEN) {
      socket.send(JSON.stringify({ type: "viewer.leave" } satisfies ClientMessage));
    }
    pendingOps.length = 0;
    pendingTransient.length = 0;
    socket?.close();
    socket = null;
  };

  const sendMessage = (message: ClientMessage): void => {
    if (socket?.readyState !== SOCKET_OPEN) {
      enqueuePendingMessage(message);
      return;
    }
    socket.send(JSON.stringify(message));
  };

  const sendOp = (op: CanvasOp): void => {
    sendMessage({ type: "op.apply", op });
  };

  const onMessage = (handler: (message: ServerMessage) => void): (() => void) => {
    handlers.add(handler);
    return () => {
      handlers.delete(handler);
    };
  };

  const onClose = (handler: () => void): (() => void) => {
    closeHandlers.add(handler);
    return () => {
      closeHandlers.delete(handler);
    };
  };

  const onReopen = (handler: () => void): (() => void) => {
    reopenHandlers.add(handler);
    return () => {
      reopenHandlers.delete(handler);
    };
  };

  return { connect, disconnect, sendOp, sendMessage, onMessage, onClose, onReopen };

  function enqueuePendingMessage(message: ClientMessage): void {
    if (message.type === "timer.command") {
      // タイマー操作はストアが再送期限内だけ同じ commandId で再送する。
      // 期限を知らない汎用キューへ残すと、ロールバック後に遅延適用されるため破棄する。
      return;
    }
    if (isPersistentMessage(message)) {
      // 永続 op は一切捨てない。切断が長期化して再接続が上限回数に達した場合は、フック側が
      // エラー状態へ遷移して全画面リロードで復旧するため、このキューが青天井に伸び続けることはない。
      pendingOps.push(message);
      return;
    }
    // 一時メッセージ（CanvasStage マウント直後の初回 viewer.sync・カーソル・view・ペン途中など）。
    // 上限超過時は古いものから捨てる（最新だけ意味があり、再接続時に作り直されるため）。
    pendingTransient.push(message);
    if (pendingTransient.length > maxPendingTransientMessages) {
      pendingTransient.shift();
    }
  }

  function flushPendingMessages(): void {
    // 永続 op を先に、続けて一時メッセージを送る。
    // 1 件ずつ送信し、送信途中で切断された場合は残りをキューに残す（splice で先取りして
    // 途中 close により未送信分を取りこぼすのを防ぐ）。残った分は次の (再)接続で送り直される。
    while (pendingOps.length > 0) {
      if (socket?.readyState !== SOCKET_OPEN) return;
      socket.send(JSON.stringify(pendingOps[0]));
      pendingOps.shift();
    }
    while (pendingTransient.length > 0) {
      if (socket?.readyState !== SOCKET_OPEN) return;
      socket.send(JSON.stringify(pendingTransient[0]));
      pendingTransient.shift();
    }
  }
}
